import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import CONFIG from './CONFIG';
import { useAuth } from '../Contexts/AuthContext';

const blobCache = new Map();
const inflightBlobs = new Map();

export function useCachedBlobUrl() {
  const { getAccessTokenSilently } = useAuth();
  const { organizationId } = useParams();

  const getCachedBlobUrl = useCallback(
    async (key, type, opts = {}) => {
      const fullUrl = `${CONFIG.SERVER_CHANNEL}/v1/files/${key}?type=${type}`;
      const cacheKey = `${organizationId}:${fullUrl}`;

      if (blobCache.has(cacheKey)) return blobCache.get(cacheKey);
      if (opts.signal?.aborted) return null;

      let entry = inflightBlobs.get(cacheKey);
      if (!entry) {
        const controller = new AbortController();
        entry = { controller, refs: 0 };
        inflightBlobs.set(cacheKey, entry);
        entry.promise = (async () => {
          try {
            const token = await getAccessTokenSilently();

            const response = await axios.get(fullUrl, {
              responseType: 'blob',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Organization-Key': organizationId,
              },
              signal: controller.signal,
            });

            if (controller.signal.aborted) return null;

            const blobUrl = URL.createObjectURL(response.data);
            blobCache.set(cacheKey, blobUrl);
            return blobUrl;
          } catch (err) {
            if (!axios.isCancel(err)) console.error('Error fetching blob:', err);
            return null;
          } finally {
            if (inflightBlobs.get(cacheKey) === entry) inflightBlobs.delete(cacheKey);
          }
        })();
      }

      entry.refs += 1;
      if (!opts.signal) return entry.promise;

      const sharedEntry = entry;
      return new Promise((resolve) => {
        const onAbort = () => {
          sharedEntry.refs -= 1;
          if (sharedEntry.refs === 0) {
            if (inflightBlobs.get(cacheKey) === sharedEntry) inflightBlobs.delete(cacheKey);
            sharedEntry.controller.abort();
          }
          resolve(null);
        };
        opts.signal.addEventListener('abort', onAbort, { once: true });
        sharedEntry.promise.then((blobUrl) => {
          opts.signal.removeEventListener('abort', onAbort);
          resolve(blobUrl);
        });
      });
    },
    [getAccessTokenSilently, organizationId],
  );

  const getArtifactUrl = useCallback(
    async (key, type, opts = {}) => {
      const fullUrl = `${CONFIG.SERVER_ORGANIZATION}/v1/artifacts/file/${key}`;

      try {
        const token = await getAccessTokenSilently();

        const response = await axios.get(fullUrl, {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Organization-Key': organizationId,
          },
          signal: opts.signal,
        });

        return URL.createObjectURL(response.data);
      } catch (err) {
        console.error('Error fetching blob:', err);
        return null;
      }
    },
    [getAccessTokenSilently, organizationId],
  );

  const getPublicArtifactAddressUrl = useCallback(
    async (key, type, opts = {}) => `${CONFIG.SERVER_ORGANIZATION}/v1/artifacts/public_file/${key}`,
    [],
  );

  const getPublicArtifactUrl = useCallback(
    async (key, type, opts = {}) => {
      const fullUrl = `${CONFIG.SERVER_ORGANIZATION}/v1/artifacts/public_file/${key}`;

      try {
        const token = await getAccessTokenSilently();

        const response = await axios.get(fullUrl, {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Organization-Key': organizationId,
          },
          signal: opts.signal,
        });

        return URL.createObjectURL(response.data);
      } catch (err) {
        console.error('Error fetching blob:', err);
        return null;
      }
    },
    [getAccessTokenSilently, organizationId],
  );

  const getDocumentUrl = useCallback(
    async (key, type, opts = {}) => {
      const fullUrl = `${CONFIG.SERVER_DOCUMENT}/v1/documents/document/${key}`;

      try {
        const token = await getAccessTokenSilently();

        const response = await axios.get(fullUrl, {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Organization-Key': organizationId,
          },
          signal: opts.signal,
        });

        return URL.createObjectURL(response.data);
      } catch (err) {
        console.error('Error fetching blob:', err);
        return null;
      }
    },
    [getAccessTokenSilently, organizationId],
  );

  return {
    getCachedBlobUrl,
    getArtifactUrl,
    getPublicArtifactUrl,
    getDocumentUrl,
    getPublicArtifactAddressUrl,
  };
}
